import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Card,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
  Table,
  Button,
  Tabs,
  Tab,
} from "react-bootstrap";
import moment from "moment";
import {
  getTicketThread,
  getTasksByTicketId,
  getTicketByTicketId,
  getBuildingBlockByTicket,
  getTicketMetaNames,
  getAllTicketStatuses,
  updateTicketRemark,
  deleteTicketRemark,
} from "../Api/TicketApiOrg";
import { useAuth } from "../Context/AuthContext";
import TicketEditModal from "./TicketEditModal";
import "./TicketInfoPage.css";
import { attachmentApi } from "../Api/TicketApiOrg";
import { toast } from "react-toastify";
import { Accordion } from "react-bootstrap";
import { addTicketRemark, getTicketRemarks } from "../Api/TicketApiOrg";
import { getResourceDetails, fetchResourceById } from "../Api/ResourceMaster";
import { fetchCusVenCpById } from "../Api/CusVenCpMasterApi";
import { getTeamById } from "../Api/TeamMaster";
import { getResourceByMavUserId } from "../Api/ResourceList";
import { fetchCusVenCpByCusVenType } from "../Api/CusVenCpMasterApi";
import { getPartnersByProject } from "../Api/PartnerMaster";
import { getResourcesByOrgId } from "../Api/ResourceList";
import { fetchProjectsByOrg } from "../Api/ViewProjects";
import { fetchProjectDetails } from "../Api/ViewProjects";
import { resolveNamesApi } from "../Api/TicketApiOrg";
import { getContactNames } from "../Api/TicketApiOrg";

/* ================= HELPERS ================= */

const getPriorityBadge = (priority) => {
  const variants = {
    critical: "danger",
    high: "warning",
    medium: "primary",
    low: "secondary",
    normal: "info",
  };

  return (
    <Badge bg={variants[priority?.toLowerCase()] || "secondary"}>
      {priority || "N/A"}
    </Badge>
  );
};

const formatDateTime = (date, time) => {
  if (!date) return "N/A";
  return `${moment(date).format("MMM DD, YYYY")} ${
    time?.substring(0, 8) || ""
  }`;
};

/* ================= PAGE ================= */

const TicketInfoPage = () => {
  const { ticketId } = useParams();
  const { user } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [ticketLogs, setTicketLogs] = useState([]);
  const [ticketTasks, setTicketTasks] = useState([]);

  const [ticketDetails, setTicketDetails] = useState(null);

  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);

  const [buildingBlock, setBuildingBlock] = useState(null);

  /* SAFE STATES FOR MODAL */
  const [projectOptions, setProjectOptions] = useState([]);
  const [ticketAssignees, setTicketAssignees] = useState([]);

  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const [remarks, setRemarks] = useState([]);
  const [newRemark, setNewRemark] = useState("");
  const [savingRemark, setSavingRemark] = useState(false);

  /* State for user names mapping (by mavUserId) */
  const [userNamesMap, setUserNamesMap] = useState({});

  /* State for resource names mapping (by resourceId like SSMAHA0007) */
  const [resourceNamesMap, setResourceNamesMap] = useState({});

  /* State for CusVen CP names mapping */
  const [cusVenCpCache, setCusVenCpCache] = useState({});

  /* State for contact names (GUIDs from CusVenCp) */
  const [contactNamesCache, setContactNamesCache] = useState({});

  const [assignedToOptions, setAssignedToOptions] = useState([]);

  const [projectName, setProjectName] = useState("");

  const [assigneeOptionsByProject, setAssigneeOptionsByProject] = useState({});

  // Add these state variables
  const [editingRemark, setEditingRemark] = useState(null);
  const [editRemarkText, setEditRemarkText] = useState("");
  const [deletingRemarkId, setDeletingRemarkId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [remarkToDelete, setRemarkToDelete] = useState(null);

  // Handle edit remark
  const handleEditRemark = (remark) => {
    setEditingRemark(remark);
    setEditRemarkText(remark.closureRemarks);
  };

  // Handle save edit remark
  const handleSaveEditRemark = async () => {
    if (!editRemarkText.trim()) {
      toast.warning("Remark cannot be empty");
      return;
    }

    try {
      await updateTicketRemark(editingRemark.ticketLogGuid, {
        closureRemarks: editRemarkText,
        changedBy: String(user.userId),
      });

      toast.success("Remark updated successfully");
      setEditingRemark(null);
      setEditRemarkText("");

      // Refresh remarks
      const updatedRemarks = await getTicketRemarks(ticketId);
      setRemarks(updatedRemarks);
    } catch (err) {
      toast.error("Failed to update remark");
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingRemark(null);
    setEditRemarkText("");
  };

  // Handle delete remark
  const handleDeleteRemark = (remark) => {
    setRemarkToDelete(remark);
    setShowDeleteConfirm(true);
  };

  const formatDateTime = (date, time) => {
    if (!date) return "N/A";
    return `${moment(date).format("MMM DD, YYYY")} ${
      time?.substring(0, 8) || ""
    }`;
  };

  const sanitizeHtml = (htmlContent) => {
    if (!htmlContent) return { __html: "" };

    let safeHtml = String(htmlContent);

    // CRITICAL FIX 1: Remove XML declarations and CDATA
    safeHtml = safeHtml.replace(/<\?xml[^?]*\?>/gi, "");
    safeHtml = safeHtml.replace(/<!\[CDATA\[(.*?)\]\]>/gis, "$1");

    // CRITICAL FIX 2: Fix nested HTML by extracting only the body content
    // If there are multiple nested HTML structures, extract the innermost body
    if (safeHtml.match(/<html.*?>/gi) && safeHtml.match(/<body.*?>/gi)) {
      const bodyMatch = safeHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
      if (bodyMatch && bodyMatch[1]) {
        safeHtml = bodyMatch[1];
      }
    }

    // Remove problematic cid: image references
    safeHtml = safeHtml.replace(/cid:[^\s"'<>]+/gi, "");

    // Remove any script tags and their contents
    safeHtml = safeHtml.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gis,
      "",
    );

    // Remove any style tags (often cause issues with nested content)
    safeHtml = safeHtml.replace(
      /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gis,
      "",
    );

    // Remove any iframe tags
    safeHtml = safeHtml.replace(
      /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gis,
      "",
    );

    // Remove dangerous on* attributes
    safeHtml = safeHtml.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, "");

    // Remove javascript: urls
    safeHtml = safeHtml.replace(/javascript:/gi, "");

    // Remove empty style attributes
    safeHtml = safeHtml.replace(/style\s*=\s*["'][^"']*["']/gi, "");

    // Fix broken img tags
    safeHtml = safeHtml.replace(
      /<img[^>]*src=["']cid:[^"']*["'][^>]*>/gi,
      '<div class="broken-image-placeholder">[Image not available]</div>',
    );

    safeHtml = safeHtml.replace(
      /<img[^>]*src=["']\s*["'][^>]*>/gi,
      '<div class="broken-image-placeholder">[Image not available]</div>',
    );

    // Ensure all img tags have valid alt text
    safeHtml = safeHtml.replace(/<img([^>]*)>/gi, (match, attrs) => {
      if (!attrs.includes("alt=")) {
        return `<img ${attrs} alt="Image">`;
      }
      return match;
    });

    // Remove any empty paragraph tags
    safeHtml = safeHtml.replace(/<p>\s*<\/p>/gi, "");

    // Fix nested div structures that might break
    safeHtml = safeHtml.replace(/<div[^>]*>\s*<div[^>]*>/gi, "<div>");
    safeHtml = safeHtml.replace(/<\/div>\s*<\/div>/gi, "</div>");

    // Handle extremely long content (over 500KB)
    if (safeHtml.length > 500000) {
      safeHtml = safeHtml.substring(0, 500000) + "... (content truncated)";
    }

    // Ensure we have valid HTML by wrapping if needed
    if (!safeHtml.trim().startsWith("<")) {
      safeHtml = `<div class="email-content">${safeHtml}</div>`;
    }

    return { __html: safeHtml };
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!remarkToDelete) return;

    try {
      setDeletingRemarkId(remarkToDelete.ticketLogGuid);
      await deleteTicketRemark(
        remarkToDelete.ticketLogGuid,
        String(user.userId),
      );

      toast.success("Remark deleted successfully");
      setShowDeleteConfirm(false);
      setRemarkToDelete(null);

      // Refresh remarks
      const updatedRemarks = await getTicketRemarks(ticketId);
      setRemarks(updatedRemarks);
    } catch (err) {
      toast.error("Failed to delete remark");
    } finally {
      setDeletingRemarkId(null);
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setRemarkToDelete(null);
  };

  // State for resolved meta names (Type, Reason, Category)
  const [resolvedNames, setResolvedNames] = useState({
    types: {},
    reasons: {},
    categories: {},
  });

  const [statusMasterData, setStatusMasterData] = useState({
    statusMap: {},
    subStatusMap: {},
    allStatuses: [],
  });

  // Helper to check if a string is a valid GUID
  const isValidGuid = (guid) => {
    if (!guid || typeof guid !== "string") return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      guid,
    );
  };

  // Resolve ticket type from GUID to name
  const resolveTicketType = (guid) => {
    if (!guid) return "N/A";
    const key = guid.toLowerCase();
    return resolvedNames.types[key] || guid.substring(0, 8);
  };

  // Resolve ticket reason from GUID to name
  const resolveTicketReason = (guid) => {
    if (!guid) return "N/A";
    const key = guid.toLowerCase();
    return resolvedNames.reasons[key] || guid.substring(0, 8);
  };

  // Resolve category from GUID to name
  const resolveCategoryName = (guid) => {
    if (!guid) return "N/A";
    const key = guid.toLowerCase();
    return resolvedNames.categories[key] || guid.substring(0, 8);
  };

  // Resolve status from GUID to name
  const resolveStatusName = (statusGuid) => {
    if (!statusGuid) return "N/A";
    if (!isValidGuid(statusGuid)) return statusGuid;
    return (
      statusMasterData.statusMap[statusGuid.toLowerCase()] ||
      statusGuid.substring(0, 8)
    );
  };

  // Resolve sub-status from GUID to name
  const resolveSubStatusName = (subStatusGuid) => {
    if (!subStatusGuid) return "N/A";
    if (!isValidGuid(subStatusGuid)) return subStatusGuid;
    return (
      statusMasterData.subStatusMap[subStatusGuid.toLowerCase()] ||
      subStatusGuid.substring(0, 8)
    );
  };

  // ============== FIXED: Resolve contact name (handles all types) ==============
  const resolveContactName = (guid) => {
    if (!guid) return "N/A";

    // Check if it's an email address
    if (guid.includes("@")) return guid;

    // Check if it's a regular name (contains space, not a CVCP ID)
    if (guid.includes(" ") && !guid.startsWith("CVCP")) return guid;

    // Check if it's a CVCP ID
    if (guid.startsWith("CVCP")) {
      return cusVenCpCache[guid] || guid;
    }

    // Check if it's a GUID format (contact person from CusVenCp)
    if (isValidGuid(guid)) {
      const contactName = contactNamesCache[guid.toLowerCase()];
      if (contactName) return contactName;
      // If not found, return truncated GUID
      return guid.substring(0, 8);
    }

    // Check if it's a numeric user ID (mavUserId)
    const numericId = parseInt(guid);
    if (!isNaN(numericId)) {
      return userNamesMap[numericId] || `User ${guid}`;
    }

    // Check if it's a resource ID (like SSMAHA0007)
    if (resourceNamesMap[guid]) return resourceNamesMap[guid];

    // Return as is if nothing matches
    return guid;
  };

  // Resolve multiple assignees (handles comma-separated values)
  const resolveAssigneeNames = (ids) => {
    if (!ids) return "N/A";
    const idArray =
      typeof ids === "string" ? ids.split(",").map((i) => i.trim()) : ids;
    return idArray.map((id) => resolveContactName(id)).join(", ");
  };

  // Get status badge with proper name
  const getStatusBadgeWithName = (statusGuid) => {
    const statusName = resolveStatusName(statusGuid);
    const variants = {
      Open: "success",
      "In Progress": "warning",
      Resolved: "info",
      Closed: "secondary",
    };
    const variant = variants[statusName] || "secondary";
    return <Badge bg={variant}>{statusName}</Badge>;
  };

  // Get sub-status badge
  const getSubStatusBadge = (subStatusGuid) => {
    const subStatusName = resolveSubStatusName(subStatusGuid);
    return <Badge bg="secondary">{subStatusName}</Badge>;
  };

  // Fetch meta names (Ticket Type, Reason, Category)
  const resolveAllTicketNames = useCallback(async (ticketData) => {
    if (!ticketData) return;

    const typeSet = new Set();
    const reasonSet = new Set();
    const categorySet = new Set();

    if (ticketData.ticketType && isValidGuid(ticketData.ticketType)) {
      typeSet.add(ticketData.ticketType);
    }
    if (ticketData.ticketReason && isValidGuid(ticketData.ticketReason)) {
      reasonSet.add(ticketData.ticketReason);
    }
    if (ticketData.category && isValidGuid(ticketData.category)) {
      categorySet.add(ticketData.category);
    }

    if (typeSet.size === 0 && reasonSet.size === 0 && categorySet.size === 0) {
      return;
    }

    try {
      const typeGuidsStr =
        typeSet.size > 0 ? Array.from(typeSet).join(",") : null;
      const reasonGuidsStr =
        reasonSet.size > 0 ? Array.from(reasonSet).join(",") : null;
      const categoryGuidsStr =
        categorySet.size > 0 ? Array.from(categorySet).join(",") : null;

      const data = await getTicketMetaNames(
        typeGuidsStr,
        reasonGuidsStr,
        categoryGuidsStr,
      );

      const typeMap = {};
      const reasonMap = {};
      const categoryMap = {};

      data.forEach((item) => {
        const key = item.guid.toLowerCase();
        if (item.entity === "TYPE") typeMap[key] = item.name;
        if (item.entity === "LOG") reasonMap[key] = item.name;
        if (item.entity === "CATEGORY") categoryMap[key] = item.name;
      });

      setResolvedNames({
        types: typeMap,
        reasons: reasonMap,
        categories: categoryMap,
      });
    } catch (err) {
      console.error("Failed to resolve meta names", err);
    }
  }, []);

  // Load status master data
  const loadStatusMasterData = async () => {
    try {
      const statuses = await getAllTicketStatuses();
      const statusMap = {};
      const subStatusMap = {};

      statuses.forEach((mainStatus) => {
        statusMap[mainStatus.statusGuid.toLowerCase()] = mainStatus.statusName;
        if (mainStatus.subStatuses && mainStatus.subStatuses.length > 0) {
          mainStatus.subStatuses.forEach((subStatus) => {
            subStatusMap[subStatus.statusGuid.toLowerCase()] =
              subStatus.statusName;
          });
        }
      });

      setStatusMasterData({
        statusMap,
        subStatusMap,
        allStatuses: statuses,
      });
    } catch (error) {
      console.error("Failed to load status master data:", error);
    }
  };

  // Fetch contact names for GUIDs
  const fetchContactNames = async (contactGuids) => {
    if (!contactGuids || contactGuids.length === 0) return;

    const guidsToFetch = contactGuids.filter(
      (guid) =>
        guid &&
        !contactNamesCache[guid.toLowerCase()] &&
        guid.match(
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
        ),
    );

    if (guidsToFetch.length === 0) return;

    try {
      const guidString = guidsToFetch.join(",");

      // ✅ USE SAME API AS TABLE PAGE
      const contacts = await getContactNames(guidsToFetch);

      const newCache = { ...contactNamesCache };

      contacts.forEach((c) => {
        if (c.contactGuid && c.contactName) {
          newCache[c.contactGuid.toLowerCase()] = c.contactName;
        }
      });

      setContactNamesCache(newCache);
    } catch (error) {
      console.error("Failed to fetch contact names:", error);
    }
  };

  // Fetch resource user names (by numeric userId)
  const fetchResourceUserNames = async (userIds) => {
    try {
      const uniqueUserIds = [...new Set(userIds)];
      const userPromises = uniqueUserIds.map(async (userId) => {
        try {
          const response = await getResourceDetails({ mavUserId: userId });
          if (response?.resource) {
            return { userId, name: response.resource.name };
          }
          return { userId, name: `User ${userId}` };
        } catch (error) {
          return { userId, name: `User ${userId}` };
        }
      });

      const userResults = await Promise.all(userPromises);
      const namesMap = {};
      userResults.forEach(({ userId, name }) => {
        namesMap[userId] = name;
      });
      setUserNamesMap((prev) => ({ ...prev, ...namesMap }));
    } catch (error) {
      console.error("Failed to fetch user names:", error);
    }
  };

  // Fetch resource names (by resourceId)
  const fetchResourceNames = async (resourceIds) => {
    try {
      const uniqueResourceIds = [
        ...new Set(
          resourceIds.filter(
            (id) =>
              id &&
              !id.startsWith("CVCP") &&
              !id.includes("@") &&
              !id.includes(" ") &&
              isNaN(Number(id)),
          ),
        ),
      ];

      const resourcePromises = uniqueResourceIds.map(async (resourceId) => {
        try {
          const response = await fetchResourceById(resourceId);
          if (response?.resource?.name) {
            return { resourceId, name: response.resource.name };
          }
          return { resourceId, name: resourceId };
        } catch (error) {
          return { resourceId, name: resourceId };
        }
      });

      const resourceResults = await Promise.all(resourcePromises);
      const namesMap = {};
      resourceResults.forEach(({ resourceId, name }) => {
        namesMap[resourceId] = name;
      });
      setResourceNamesMap((prev) => ({ ...prev, ...namesMap }));
    } catch (error) {
      console.error("Failed to fetch resource names:", error);
    }
  };

  // Resolve CVCP names
  const resolveCusVenCpName = async (cpId) => {
    if (!cpId || !cpId.startsWith("CVCP")) return null;
    if (cusVenCpCache[cpId]) return cusVenCpCache[cpId];

    try {
      const cp = await fetchCusVenCpById(cpId);
      if (cp?.contactPerName) {
        setCusVenCpCache((prev) => ({ ...prev, [cpId]: cp.contactPerName }));
        return cp.contactPerName;
      }
    } catch (err) {
      console.error("Failed to fetch CP:", cpId, err);
    }
    return cpId;
  };

  // Build assigned to options
  const buildAssignedToOptions = async () => {
    try {
      if (!user?.organisationId) return;
      const res = await getResourcesByOrgId(user.organisationId);
      const resources = Array.isArray(res) ? res : res?.resources || [];
      const options = resources.map((r) => ({
        value: r.resourceId,
        label: r.name,
      }));
      setAssignedToOptions(options);
    } catch (error) {
      console.error("Failed to load assignees", error);
    }
  };

  // Fetch project and partners for assignees
  const fetchProjectAndPartners = async (projectId) => {
    try {
      if (assigneeOptionsByProject[projectId]) return;

      const localOptions = [];
      const projectDetails = await fetchProjectDetails(projectId);

      if (projectDetails?.teamAssigned) {
        const teamData = await getTeamById(projectDetails.teamAssigned);
        if (Array.isArray(teamData?.teamMembers)) {
          for (const memberId of teamData.teamMembers) {
            const res = await getResourceByMavUserId(memberId);
            const resource = res?.resource;
            if (resource?.resourceId) {
              localOptions.push({
                value: resource.resourceId,
                label: `${resource.name} (Team)`,
              });
            }
          }
        }
      }

      if (projectDetails?.prClient) {
        try {
          const clientCPs = await fetchCusVenCpByCusVenType(
            projectDetails.prClient,
          );
          if (Array.isArray(clientCPs)) {
            clientCPs.forEach((cp) => {
              if (cp?.contactPerName && cp?.cusVenId) {
                localOptions.push({
                  value: cp.cusVenId,
                  label: `${cp.contactPerName} (Client Contact)`,
                });
              }
            });
          }
        } catch (error) {
          console.log("No client contact persons found");
        }
      }

      try {
        const partners = await getPartnersByProject(projectId);
        if (Array.isArray(partners)) {
          for (const partner of partners) {
            if (partner?.partnerName) {
              try {
                const partnerCPs = await fetchCusVenCpByCusVenType(
                  partner.partnerName,
                );
                if (Array.isArray(partnerCPs)) {
                  partnerCPs.forEach((cp) => {
                    if (cp?.contactPerName && cp?.cusVenId) {
                      localOptions.push({
                        value: cp.cusVenId,
                        label: `${cp.contactPerName} (Partner Contact)`,
                      });
                    }
                  });
                }
              } catch (error) {
                console.log(
                  `No contact persons for partner: ${partner.partnerName}`,
                );
              }
            }
          }
        }
      } catch (error) {
        console.log("No partners found for project");
      }

      setAssigneeOptionsByProject((prev) => ({
        ...prev,
        [projectId]: localOptions,
      }));
    } catch (error) {
      console.error("Failed to fetch project assignees", error);
    }
  };

  // Fetch projects
  const fetchProjects = async () => {
    try {
      if (!user?.organisationId) return;
      const data = await fetchProjectsByOrg(user.organisationId);
      const options = (data || []).map((p) => ({
        value: p.projectId,
        label: `${p.prTitle} (${p.projectId})`,
      }));
      setProjectOptions(options);
    } catch (err) {
      console.error("Failed to load projects", err);
    }
  };

  // Load project name
  const loadProjectName = async (projectId) => {
    if (!projectId) return;
    try {
      const res = await fetchProjectDetails(projectId);
      setProjectName(res?.prTitle || projectId);
    } catch (error) {
      setProjectName(projectId);
    }
  };

  // Handle edit click
  const handleEditClick = async () => {
    if (ticket?.projectAssigned) {
      const projectIds = ticket.projectAssigned
        .split(",")
        .map((id) => id.trim());
      let allAssignees = [];

      for (const pid of projectIds) {
        let assignees = assigneeOptionsByProject[pid];
        if (!assignees || assignees.length === 0) {
          await fetchProjectAndPartners(pid);
          assignees = assigneeOptionsByProject[pid] || [];
        }
        allAssignees.push(...assignees);
      }

      const unique = new Map();
      allAssignees.forEach((a) => unique.set(a.value, a));
      setTicketAssignees(Array.from(unique.values()));
    }
    setShowEditModal(true);
  };

  // Get all from emails
  const getAllFromEmails = () => {
    if (!messages || messages.length === 0) return "N/A";
    const emails = messages.map((m) => m.fromMail).filter(Boolean);
    const uniqueEmails = [...new Set(emails)];
    return uniqueEmails.join(", ");
  };

  // Filter work logs
  const getWorkLogs = () => {
    return ticketLogs.filter((log) => log.actionType === "WORK_LOG");
  };

  const getNonWorkLogActivities = () => {
    return ticketLogs.filter((log) => log.actionType !== "WORK_LOG");
  };

  // Get file icon
  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return "📄";
      case "doc":
      case "docx":
        return "📝";
      case "xls":
      case "xlsx":
        return "📊";
      case "jpg":
      case "jpeg":
      case "png":
        return "🖼️";
      case "zip":
        return "📦";
      default:
        return "📎";
    }
  };

  // Handle add remark
  const handleAddRemark = async () => {
    if (!newRemark.trim()) {
      toast.warning("Remark cannot be empty");
      return;
    }

    try {
      setSavingRemark(true);
      await addTicketRemark({
        ticketId: ticketId,
        orgId: user.organisationId,
        closureRemarks: newRemark,
        actionType: "REMARK",
        createdBy: String(user.userId),
        changedBy: String(user.userId),
      });
      toast.success("Remark added");
      setNewRemark("");
      const updatedRemarks = await getTicketRemarks(ticketId);
      setRemarks(updatedRemarks);
    } catch (err) {
      toast.error("Failed to add remark");
    } finally {
      setSavingRemark(false);
    }
  };

  // Handle download attachment
  const handleDownloadAttachment = async (blobUrl, fileName) => {
    try {
      const response = await attachmentApi.downloadAttachment(
        blobUrl,
        fileName,
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download attachment");
    }
  };

  // Handle view attachment
  const handleViewAttachment = async (blobUrl, fileName) => {
    try {
      const response = await attachmentApi.viewAttachment(blobUrl, fileName);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error("Failed to view attachment");
    }
  };

  // Collect and fetch all IDs for name resolution
  const collectAndFetchIds = async (details, logs, tasks) => {
    const userIds = new Set();
    const resourceIds = new Set();
    const cpIds = new Set();
    const contactGuids = new Set();

    const addId = (id) => {
      if (!id) return;
      const trimmedId = id.trim();

      if (trimmedId.includes("@")) return;
      if (trimmedId.includes(" ") && !trimmedId.startsWith("CVCP")) return;

      if (trimmedId.startsWith("CVCP")) {
        cpIds.add(trimmedId);
        return;
      }

      if (isValidGuid(trimmedId)) {
        contactGuids.add(trimmedId);
        return;
      }

      const numericId = parseInt(trimmedId);
      if (!isNaN(numericId)) {
        userIds.add(numericId);
        return;
      }

      resourceIds.add(trimmedId);
    };

    if (details?.assignedTo) {
      details.assignedTo.split(",").forEach(addId);
    }
    if (details?.submittedBy) {
      details.submittedBy.split(",").forEach(addId);
    }
    if (details?.createdBy) {
      addId(details.createdBy);
    }

    logs?.forEach((log) => {
      if (log.changedBy) addId(log.changedBy);
    });

    tasks?.forEach((task) => {
      if (task.assignedTo) addId(task.assignedTo);
    });

    await Promise.all([
      userIds.size > 0
        ? fetchResourceUserNames(Array.from(userIds))
        : Promise.resolve(),
      resourceIds.size > 0
        ? fetchResourceNames(Array.from(resourceIds))
        : Promise.resolve(),
      ...Array.from(cpIds).map((id) => resolveCusVenCpName(id)),
      contactGuids.size > 0
        ? fetchContactNames(Array.from(contactGuids))
        : Promise.resolve(),
    ]);
  };

  // Load all data
  const loadData = async () => {
    try {
      setLoading(true);

      const thread = await getTicketThread(ticketId);
      setTicket(thread?.ticket || null);
      // ✅ Add validation for messages
      const validMessages = (thread?.messages || []).map((msg) => ({
        ...msg,
        rawHtmlBody: msg.rawHtmlBody || msg.body || "",
        body: msg.body || "",
      }));
      setMessages(validMessages);
      setTicketLogs(thread?.logs || []);

      try {
        const tasks = await getTasksByTicketId(ticketId);
        setTicketTasks(tasks || []);
      } catch (error) {
        setTicketTasks([]);
      }

      const details = await getTicketByTicketId(ticketId);
      setTicketDetails(details);

      if (details?.projectAssigned) {
        await loadProjectName(details.projectAssigned);
      }

      await loadStatusMasterData();
      await resolveAllTicketNames(details);

      if (details?.projectAssigned) {
        const projectIds = details.projectAssigned
          .split(",")
          .map((id) => id.trim());
        for (const pid of projectIds) {
          await fetchProjectAndPartners(pid);
        }
      }

      const tasksList = await getTasksByTicketId(ticketId).catch(() => []);
      await collectAndFetchIds(details, thread?.logs, tasksList);

      try {
        const block = await getBuildingBlockByTicket(ticketId);
        setBuildingBlock(block);
      } catch (error) {
        setBuildingBlock(null);
      }

      const remarksData = await getTicketRemarks(ticketId);
      setRemarks(remarksData || []);

      const attachmentData = await attachmentApi.getTicketAttachments(ticketId);
      if (attachmentData?.attachments) {
        setTicketAttachments(attachmentData.attachments);
      }
    } catch (err) {
      console.error("Failed to load ticket", err);
      toast.error("Failed to load ticket details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.organisationId) {
      buildAssignedToOptions();
      fetchProjects();
      loadData();
    }
  }, [ticketId, user]);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner />
      </div>
    );
  }

  if (!ticket) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">Ticket not found</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="tdp-container">
      {/* HEADER */}
      <Card className="tdp-header-card shadow-sm mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={9}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "81vw",
                  alignItems: "center",
                }}
              >
                <h4>{ticket.ticketName}</h4>
                <Button onClick={handleEditClick}>✏ Edit Ticket</Button>
              </div>

              <div className="d-flex gap-2 mt-2">
                <Badge>{ticket.ticketId}</Badge>
                {getStatusBadgeWithName(ticketDetails?.status)}
                {getPriorityBadge(ticket.priority)}
              </div>

              <div className="mt-2 opacity-75">
                Created by <b>{resolveContactName(ticket.createdBy)}</b> •{" "}
                {moment(ticket.ticketTime).format("MMM DD YYYY HH:mm")}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* TABS */}
      <Card className="tdp-main-card shadow-sm">
        <Card.Body>
          <Tabs defaultActiveKey="overview">
            {/* OVERVIEW TAB */}
            <Tab eventKey="overview" title="Overview">
              <Row className="mt-3 g-3">
                <Col md={6}>
                  <Card className="tdp-info-card">
                    <Card.Body>
                      <h6 className="mb-3">Ticket Information</h6>
                      <Row>
                        <Col sm={6}>
                          <p>
                            <b>Ticket ID</b>
                            <br />
                            {ticketDetails?.ticketId}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Ticket Short ID</b>
                            <br />
                            {ticketDetails?.ticketShortId}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Status</b>
                            <br />
                            {getStatusBadgeWithName(ticketDetails?.status)}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Sub Status</b>
                            <br />
                            {getSubStatusBadge(ticketDetails?.subStatus)}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Priority</b>
                            <br />
                            {getPriorityBadge(ticketDetails?.priority)}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Category</b>
                            <br />
                            {resolveCategoryName(ticketDetails?.category)}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Ticket Type</b>
                            <br />
                            {resolveTicketType(ticketDetails?.ticketType)}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Reason</b>
                            <br />
                            {resolveTicketReason(ticketDetails?.ticketReason)}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Project</b>
                            <br />
                            {projectName || "N/A"}
                          </p>
                        </Col>
                        <Col sm={6}>
                          <p>
                            <b>Due Date</b>
                            <br />
                            {ticketDetails?.dueDate
                              ? moment(ticketDetails.dueDate).format(
                                  "MMM DD YYYY",
                                )
                              : "N/A"}
                          </p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="tdp-info-card">
                    <Card.Body>
                      <h6 className="mb-3">People & Email</h6>
                      <Row>
                        <Col sm={12}>
                          <p>
                            <b>Created By</b>
                            <br />
                            {resolveContactName(ticketDetails?.createdBy)}
                          </p>
                        </Col>
                        <Col sm={12}>
                          <p>
                            <b>Assigned To</b>
                            <br />
                            {resolveAssigneeNames(ticketDetails?.assignedTo) ||
                              "Unassigned"}
                          </p>
                        </Col>
                        <Col sm={12}>
                          <p>
                            <b>Submitted By</b>
                            <br />
                            {resolveContactName(ticketDetails?.submittedBy) ||
                              "N/A"}
                          </p>
                        </Col>
                        <Col sm={12}>
                          <p>
                            <b>Email Source</b>
                            <br />
                            {getAllFromEmails()}
                          </p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={12}>
                  <Card className="tdp-info-card">
                    <Card.Body>
                      <h6 className="mb-3">Attachments</h6>
                      {loadingAttachments ? (
                        <Spinner />
                      ) : ticketAttachments.length > 0 ? (
                        ticketAttachments.map((att, i) => (
                          <Card key={i} className="mb-2">
                            <Card.Body className="py-2 d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <span
                                  style={{ fontSize: "20px" }}
                                  className="me-2"
                                >
                                  {getFileIcon(att.fileName)}
                                </span>
                                <div>
                                  <div className="fw-bold">{att.fileName}</div>
                                  <small className="text-muted">
                                    Uploaded by {att.createdBy}
                                  </small>
                                </div>
                              </div>
                              <div>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() =>
                                    handleViewAttachment(
                                      att.azureBlobUrl,
                                      att.fileName,
                                    )
                                  }
                                >
                                  👁️ View
                                </Button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() =>
                                    handleDownloadAttachment(
                                      att.azureBlobUrl,
                                      att.fileName,
                                    )
                                  }
                                >
                                  ⬇️ Download
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        ))
                      ) : (
                        <p className="text-muted">No attachments found</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

            {/* REMARKS TAB */}
            {/* REMARKS TAB */}
            <Tab eventKey="remarks" title="Remarks">
              <div className="mt-3">
                {/* Add Remark Form */}
                <Card className="mb-3">
                  <Card.Body>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Add remark..."
                      value={newRemark}
                      onChange={(e) => setNewRemark(e.target.value)}
                    />
                    <div className="text-end mt-2">
                      <Button onClick={handleAddRemark} disabled={savingRemark}>
                        {savingRemark ? "Saving..." : "Add Remark"}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>

                {/* Edit Remark Modal */}
                {editingRemark && (
                  <div
                    className="modal show d-block"
                    tabIndex="-1"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                  >
                    <div className="modal-dialog">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title">Edit Remark</h5>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={handleCancelEdit}
                          ></button>
                        </div>
                        <div className="modal-body">
                          <textarea
                            className="form-control"
                            rows="4"
                            value={editRemarkText}
                            onChange={(e) => setEditRemarkText(e.target.value)}
                          />
                        </div>
                        <div className="modal-footer">
                          <Button
                            variant="secondary"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            onClick={handleSaveEditRemark}
                          >
                            Save Changes
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                  <div
                    className="modal show d-block"
                    tabIndex="-1"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                  >
                    <div className="modal-dialog">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title">Delete Remark</h5>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={handleCancelDelete}
                          ></button>
                        </div>
                        <div className="modal-body">
                          <p>Are you sure you want to delete this remark?</p>
                          <div className="alert alert-secondary">
                            <em>"{remarkToDelete?.closureRemarks}"</em>
                          </div>
                          <p className="text-danger small">
                            This action cannot be undone.
                          </p>
                        </div>
                        <div className="modal-footer">
                          <Button
                            variant="secondary"
                            onClick={handleCancelDelete}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="danger"
                            onClick={handleConfirmDelete}
                            disabled={
                              deletingRemarkId === remarkToDelete?.ticketLogGuid
                            }
                          >
                            {deletingRemarkId === remarkToDelete?.ticketLogGuid
                              ? "Deleting..."
                              : "Delete"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Remarks List */}
                {remarks.length > 0 ? (
                  remarks.map((remark) => (
                    <Card key={remark.ticketLogGuid} className="mb-2">
                      <Card.Body>
                        <div className="d-flex justify-content-between">
                          <div>
                            <strong>
                              {resolveContactName(remark.createdBy)}
                            </strong>
                            <div className="text-muted small">
                              {formatDateTime(
                                remark.createdDate,
                                remark.createdTime,
                              )}
                            </div>
                          </div>
                          <div>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => handleEditRemark(remark)}
                            >
                              ✏️ Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteRemark(remark)}
                            >
                              🗑️ Delete
                            </Button>
                          </div>
                        </div>
                        <div className="mt-2">{remark.closureRemarks}</div>
                        {/* Show edit/delete info if remark was modified */}
                        {remark.changedDate &&
                          remark.changedDate !== remark.createdDate && (
                            <div className="text-muted small mt-2">
                              <em>
                                Last edited:{" "}
                                {formatDateTime(
                                  remark.changedDate,
                                  remark.changedTime,
                                )}
                                {remark.changedBy &&
                                  ` by ${resolveContactName(remark.changedBy)}`}
                              </em>
                            </div>
                          )}
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <p className="text-muted">No remarks yet</p>
                )}
              </div>
            </Tab>

            {/* CONVERSATION TAB */}
            <Tab eventKey="conversation" title="Conversation">
              <div className="mt-3">
                {messages.length > 0 ? (
                  <Accordion
                    alwaysOpen={false}
                    className="tdp-conversation-accordion"
                  >
                    {messages.map((msg, index) => (
                      <Accordion.Item
                        eventKey={index.toString()}
                        key={msg.messageGuid}
                        className="tdp-message-accordion"
                      >
                        <Accordion.Header>
                          <div className="conversation-header">
                            <div>
                              <strong>{msg.fromName || msg.fromMail}</strong>
                              <div className="text-muted small">
                                {moment(msg.createdOnDate).format(
                                  "MMM DD YYYY",
                                )}{" "}
                                {msg.createdOnTime?.substring(0, 8)}
                              </div>
                            </div>
                            {msg.isInternal && (
                              <Badge bg="warning" className="ms-2">
                                Internal
                              </Badge>
                            )}
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          {(() => {
                            try {
                              const content = msg.rawHtmlBody || msg.body;

                              // Log for debugging
                              if (content) {
                                console.log(
                                  `Rendering message ${msg.messageGuid}:`,
                                  {
                                    hasHtml: !!msg.rawHtmlBody,
                                    htmlLength: msg.rawHtmlBody?.length,
                                    hasCid: msg.rawHtmlBody?.includes("cid:"),
                                    hasNestedHtml:
                                      msg.rawHtmlBody?.includes("<html") &&
                                      msg.rawHtmlBody?.includes("<body"),
                                  },
                                );
                              }

                              if (!content || content.trim() === "") {
                                return (
                                  <div className="text-muted">No content</div>
                                );
                              }

                              return (
                                <div
                                  dangerouslySetInnerHTML={sanitizeHtml(
                                    content,
                                  )}
                                />
                              );
                            } catch (error) {
                              console.error(
                                "Failed to render message:",
                                error,
                                msg,
                              );
                              return (
                                <div className="alert alert-warning">
                                  <small>
                                    Unable to display this message content
                                  </small>
                                  {msg.body && (
                                    <pre
                                      className="mt-2 small"
                                      style={{ whiteSpace: "pre-wrap" }}
                                    >
                                      {msg.body.substring(0, 500)}
                                    </pre>
                                  )}
                                </div>
                              );
                            }
                          })()}
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                ) : (
                  <p>No messages</p>
                )}
              </div>
            </Tab>

            {/* WORK LOG TAB */}
            <Tab eventKey="worklog" title="Work Log">
              <Table bordered hover className="tdp-activity-table mt-3">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Action</th>
                    <th>User</th>
                    <th>Effort</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {getWorkLogs().length > 0 ? (
                    getWorkLogs().map((log, i) => (
                      <tr key={log.ticketLogGuid}>
                        <td>{i + 1}</td>
                        <td>
                          <Badge bg="info">
                            {log.actionType?.replace(/_/g, " ")}
                          </Badge>
                        </td>
                        <td>{resolveContactName(log.changedBy)}</td>
                        <td>
                          {log.effortValue
                            ? `${log.effortValue} ${log.effortUnit || "HOURS"}`
                            : "-"}
                        </td>
                        <td>
                          {formatDateTime(log.createdDate, log.createdTime)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No work log entries found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Tab>

            {/* ACTIVITY LOG TAB */}
            <Tab eventKey="activity" title="Activity Log">
              <Table bordered hover className="tdp-activity-table mt-3">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Action</th>
                    <th>Old</th>
                    <th>New</th>
                    <th>User</th>
                    <th>Effort</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {getNonWorkLogActivities().length > 0 ? (
                    getNonWorkLogActivities().map((log, i) => (
                      <tr key={log.ticketLogGuid}>
                        <td>{i + 1}</td>
                        <td>
                          <Badge bg="secondary">
                            {log.actionType?.replace(/_/g, " ")}
                          </Badge>
                        </td>
                        <td>{log.oldValue || "-"}</td>
                        <td>{log.newValue || "-"}</td>
                        <td>{resolveContactName(log.changedBy)}</td>
                        <td>
                          {log.effortValue
                            ? `${log.effortValue} ${log.effortUnit}`
                            : "-"}
                        </td>
                        <td>
                          {formatDateTime(log.createdDate, log.createdTime)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        No activity logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Tab>

            {/* TASKS TAB */}
            <Tab eventKey="tasks" title="Tasks">
              <div className="mt-3">
                {ticketTasks.length > 0 ? (
                  ticketTasks.map((task) => (
                    <Card key={task.taskId} className="tdp-task-card mb-3">
                      <Card.Body>
                        <div className="d-flex justify-content-between">
                          <div>
                            <b>{task.taskName}</b>{" "}
                            <Badge bg="dark">{task.taskId}</Badge>
                          </div>
                          <div className="d-flex gap-2">
                            {getPriorityBadge(task.taskPriority)}
                          </div>
                        </div>
                        <div className="text-muted mt-2">
                          Assigned To: {resolveAssigneeNames(task.assignedTo)}
                        </div>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <p>No related tasks</p>
                )}
              </div>
            </Tab>

            {/* BUILDING BLOCK TAB */}
            <Tab eventKey="buildingBlock" title="Building Block">
              <div className="mt-3">
                {buildingBlock ? (
                  <Table bordered hover>
                    <thead className="table-light">
                      <tr>
                        <th>Block ID</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Project</th>
                        <th>Process POC</th>
                        <th>Tech POC</th>
                        <th>Type</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{buildingBlock.buildingBlockId}</td>
                        <td>{buildingBlock.buildingBlockName}</td>
                        <td>
                          <Badge bg="secondary">
                            {buildingBlock.buildingBlockStatus}
                          </Badge>
                        </td>
                        <td>{buildingBlock.buildingBlockProject}</td>
                        <td>{buildingBlock.buildingBlockProcessPoc}</td>
                        <td>{buildingBlock.buildingBlockTechPoc}</td>
                        <td>{buildingBlock.buildingBlockTypes}</td>
                        <td>
                          {buildingBlock.actualStartDate
                            ? moment(buildingBlock.actualStartDate).format(
                                "MMM DD YYYY",
                              )
                            : "N/A"}
                        </td>
                        <td>
                          {buildingBlock.actualEndDate
                            ? moment(buildingBlock.actualEndDate).format(
                                "MMM DD YYYY",
                              )
                            : "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                ) : (
                  <p>No Building Block mapped</p>
                )}
              </div>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* EDIT MODAL */}
      <TicketEditModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        ticket={ticket}
        orgId={user?.organisationId}
        userDetails={user}
        projectOptions={projectOptions}
        ticketAssignees={ticketAssignees}
        assigneeOptionsByProject={assigneeOptionsByProject}
        fetchProjectAndPartners={fetchProjectAndPartners}
        refreshTickets={loadData}
      />
    </Container>
  );
};

export default TicketInfoPage;
